import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { first, get } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { config } from 'src/config/config';

import { CareRecommendationRecommendation } from 'src/generated/webapp_gql';
import { CrisisContainer } from 'src/modules/onboarding/components/CrisisContainer';
import { getMeditationByName } from 'src/modules/meditations/selectors/meditationSelectors';
import { getCBTProgramState } from 'src/modules/programs/cbtProgram/selectors/cbtProgramSelectors';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType, SOURCE_TYPES } from 'src/modules/user/models/user';
import { getMostRecentCareRecommendation } from 'src/modules/user/selectors/userSelectors';
import { CrisisModal } from 'src/shared/components/CrisisModal';
import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';
import arrowIcon from 'src/shared/svg/icon-arrow-back.svg';

const TileWrapper = styled.div({
    padding: 5,
    marginLeft: 15,
    marginBottom: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    border: `1px solid ${color.linkWaterLight}`,
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
        boxShadow: shadow.floatingShadow,
    },
    borderRadius: 12,
    width: '100%',
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.black,
        textTransform: 'capitalize',
    },
    font.heavy(font.size.medium),
);

interface TileDescriptionProps {
    quote?: boolean;
}

const TileDescription = styled.p<TileDescriptionProps>(
    props => ({
        textAlign: 'center',
        color: props.quote ? color.blueBayoux : color.steelGray,
        fontStyle: props.quote ? 'oblique' : 'normal',
    }),
    font.book(font.size.base),
);

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    height: '4.5rem',
    width: '4.5rem',
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    border: 'none',
}));

const ArrowIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    display: 'inline-block',
    backgroundImage: `url('${iconUrl}')`,
    height: '1.5rem',
    width: '1.5rem',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: 'center',
    border: 'none',
    marginRight: 15,
}));

const CardWrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
});

const TextWrapper = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 10,
});

interface CardProps {
    image: string;
    title: string;
    initials?: string;
    description?: string;
    duration?: number;
    extraTopSpace?: boolean;
    onClick: () => void;
}

const Card: React.FC<CardProps> = ({ image, title, description, onClick }) => {
    return (
        <TileWrapper onClick={onClick}>
            <CardWrapper>
                <StyledIcon iconUrl={image} />
                <TextWrapper>
                    {title && <TileTitle>{title}</TileTitle>}
                    <TileDescription>{description}</TileDescription>
                </TextWrapper>
            </CardWrapper>
            <ArrowIcon iconUrl={arrowIcon} />
        </TileWrapper>
    );
};

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '50%',
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
    [mediaQueries.md]: {
        marginTop: 15,
        width: '75%',
    },
});

const MainContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        marginBottom: 0,
        textAlign: 'center',
    },
    font.black(font.size.headerExtended),
);

const Description = styled.p({
    color: color.steelGray,
    maxWidth: 820,
    marginBottom: 30,
    textAlign: 'center',
    ...font.book(font.size.mediumExtended),
});

const Image = styled.img({
    maxWidth: 250,
    [mediaQueries.sm]: {
        maxWidth: 100,
    },
    [mediaQueries.md]: {
        maxWidth: 150,
    },
});

type ProxyProps = RouteComponentProps<
    {},
    never,
    {
        title?: string;
        description?: string;
        componentKey?: string;
    }
>;

export const Proxy: React.FC<ProxyProps> = ({ location }) => {
    const title = get(location, 'state.title');
    const description = get(location, 'state.description');
    const componentKey = get(location, 'state.componentKey');
    return <YoureOnYourWay title={title} description={description} componentKey={componentKey} />;
};

interface YoureOnYourWayProps {
    title?: string;
    description?: string;
    componentKey?: string;
}

export const YoureOnYourWay: React.FC<YoureOnYourWayProps> = ({
    title,
    description,
    componentKey,
}) => {
    const [showCrisisModal, setShowCrisisModal] = useState(false);

    const mostRecentCareRec = useSelector(getMostRecentCareRecommendation);
    const provider = first(useSelector(getMyProviders));
    const meditation = useSelector(state => getMeditationByName(state, 'One Minute Meditation'));
    const flags = useFlags();

    const { weeks } = useSelector(getCBTProgramState);
    const program = weeks[0];

    const { t } = useTranslation();
    const history = useHistory();

    const isDigitalCare =
        mostRecentCareRec?.recommendation === CareRecommendationRecommendation.DigitalPrograms;
    const isCircles =
        mostRecentCareRec?.recommendation === CareRecommendationRecommendation.Circles;

    const handleWorkOneonOne = () => {
        let nextPage = '/dashboard';

        switch (mostRecentCareRec?.recommendation || CareRecommendationRecommendation.Coaching) {
            case CareRecommendationRecommendation.Circles:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-coach';
                }
                break;
            case CareRecommendationRecommendation.DigitalPrograms:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-coach';
                }
                break;
            case CareRecommendationRecommendation.Coaching:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-coach';
                }
                break;
            case CareRecommendationRecommendation.MentalHealthCoaching:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-mental-health-coach';
                }
                break;
            case CareRecommendationRecommendation.Therapy:
                if (!flags?.hideSessionSchedulingWeb) {
                    nextPage = '/work-with-a-therapist';
                }
                break;
            default:
                nextPage = '/dashboard';
        }

        if (flags?.showAlreadyWorkingWithTherapist) {
            history.push('/already-working-with-therapist', { nextPage });
        } else {
            history.push(nextPage);
        }
    };

    return (
        <MainContainer>
            <Container>
                <Image src={require('../../../public/img/marginalia-virtual-reality.png')} />
                <Title>{title || t('onboarding:your_on_your_way_screen.title')}</Title>
                <Description>
                    {description || t('onboarding:your_on_your_way_screen.description')}
                </Description>
                {isDigitalCare && program ? (
                    <Card
                        description={t('onboarding:your_on_your_way_screen.program')}
                        title={program.title}
                        image={program.iconUrl}
                        duration={6}
                        onClick={() => {
                            history.push(`/programs/${ProgramEnum.CP}/${program.id}`);
                        }}
                        extraTopSpace
                    />
                ) : null}

                {!isDigitalCare && !isCircles && meditation ? (
                    <Card
                        description={t('onboarding:your_on_your_way_screen.meditation')}
                        title={meditation.name}
                        image={meditation.image}
                        duration={meditation.duration}
                        onClick={() => {
                            history.push(`/meditation/${meditation.slug}`);
                        }}
                    />
                ) : null}

                <Card
                    description={t('onboarding:your_on_your_way_screen.circles.description')}
                    title={t('onboarding:your_on_your_way_screen.circles.title')}
                    image={require('../../../public/img/marginalia-circles.png')}
                    onClick={() =>
                        window.open(
                            'https://circles.modernhealth.com/?utm_source=modern-health&utm_medium=web-app',
                            '_blank',
                        )
                    }
                />

                {isCircles && meditation ? (
                    <Card
                        description={t('onboarding:your_on_your_way_screen.meditation')}
                        title={meditation.name}
                        image={meditation.image}
                        duration={meditation.duration}
                        onClick={() => {
                            history.push(`/meditation/${meditation.slug}`);
                        }}
                    />
                ) : null}

                {provider ? (
                    <Card
                        title={t('onboarding:your_on_your_way_screen.provider.title', {
                            providerType:
                                provider.providerType === ProviderType.THERAPIST
                                    ? t('onboarding:your_on_your_way_screen.therapist')
                                    : t('onboarding:your_on_your_way_screen.coach'),
                        })}
                        description={t('onboarding:your_on_your_way_screen.provider.description')}
                        image={provider?.avatar || ''}
                        onClick={() => {
                            window.open(config.iOSAppStoreURL, '_blank');
                        }}
                    />
                ) : null}

                {!provider ? (
                    <Card
                        description={t('onboarding:your_on_your_way_screen.1on1.description')}
                        title={t('onboarding:your_on_your_way_screen.1on1.title')}
                        image={require(`../../../public/img/work_with_a_coach.png`)}
                        onClick={handleWorkOneonOne}
                    />
                ) : null}

                <Card
                    description={t('onboarding:your_on_your_way_screen.onMyOwn.description')}
                    title={t('onboarding:your_on_your_way_screen.onMyOwn.title')}
                    image={require('../../../public/img/marginalia-boat.png')}
                    onClick={() => {
                        history.push('/');
                    }}
                />
                <CrisisContainer
                    componentKey={componentKey}
                    setShowCrisisModal={setShowCrisisModal}
                />
            </Container>
            <CrisisModal
                visible={showCrisisModal}
                onClose={() => {
                    setShowCrisisModal(false);
                }}
                source={SOURCE_TYPES.PRE_ONBOARDING}
            />
        </MainContainer>
    );
};

export default Proxy;
